import { StickyStatesPlugin } from "@uirouter/sticky-states";

import { HomeComponent } from './components/home/home'
import { FullSearchComponent } from './components/full-search/full-search'
import { BookModalComponent } from './components/book-modal/book-modal'
import { GoogleViewerComponent } from './components/google-viewer/google-viewer'
import { UserManagementComponent } from './components/user-management/user-management'
import { PrivacyComponent } from './components/privacy/privacy'
import { ErrorPageComponent } from './components/error-page/error-page'
import { ExploreAuthorComponent } from './components/explore-author/explore-author' 
import { UserLibraryComponent } from './components/user-library/user-library' 
import { ShelfSettingsComponent } from './components/shelf-settings/shelf-settings' 
import { UserShelfComponent } from './components/user-shelf/user-shelf' 
import { BarcodeScannerComponent } from './components/barcode-scanner/barcode-scanner'; 

import { Ng2StateDeclaration, Transition, StatesModule, UIRouter, StateService, RedirectToResult } from '@uirouter/angular';
import { NgbModal, NgbModalRef } from '@mattlewis92/ng-bootstrap';

import { Injector, Component } from '@angular/core'

import { ShelvableBackend } from './services/ShelvableBackend/ShelvableBackend'
import { PageMetadataService } from './services/PageMetadataService'
import { GoogleData } from './services/GoogleData/GoogleData';
import { Meta, Title } from '@angular/platform-browser';

import { Analytics } from './services/Analytics'

import { SearchConfig } from './services/SearchConfigService'

import { Observable } from 'rxjs'
import { Utilities } from "./services/Utilities";
import { BehaviorSubject } from "rxjs/BehaviorSubject";

//https://www.npmjs.com/package/ng2-router-modal

function createBreadcrumbs(userName:string, displayName:string, shelf:string) {
    
    var itemListElement = [{
        "@type": "ListItem",
        "position": 1,
        "item": {
            "@id": "https://shelvable.com/",
            "name": "Shelvable"
        }
    }];
    
    if (userName) {
        itemListElement.push({
            "@type": "ListItem",
            "position": 2,
            "item": {
                "@id": "https://shelvable.com/" + userName,
                "name": displayName
            }
        });
    }

    if (shelf) {
        itemListElement.push({
            "@type": "ListItem",
            "position": 3,
            "item": {
                "@id": "https://shelvable.com/" + userName + "/" + shelf,
                "name": shelf
            }
        });
    }

    var breadcrumbs = {
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": itemListElement
    }

    return JSON.stringify(breadcrumbs);
}

function createExploreBreadcrumbs(topicName:string, topicUrl:string) {
    
    var itemListElement = [{
        "@type": "ListItem",
        "position": 1,
        "item": {
            "@id": "https://shelvable.com/",
            "name": "Shelvable"
        }
    }];
    
    itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "item": {
            "@id": "https://shelvable.com/e" + topicUrl,
            "name": topicName
        }
    });

    var breadcrumbs = {
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": itemListElement
    }

    return JSON.stringify(breadcrumbs);
}

function createBookBreadcrumbs(googleId:string, authors:string[], title:string) {
    
    var itemListElement = [{
        "@type": "ListItem",
        "position": 1,
        "item": {
            "@id": "https://shelvable.com/",
            "name": "Shelvable"
        }
    }];
    
    itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "item": {
            "@id": "https://shelvable.com/b" + googleId,
            "name": title + (authors ? (' by ' + authors.join(', ')) : '')
        }
    });

    var breadcrumbs = {
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": itemListElement
    }

    return JSON.stringify(breadcrumbs);
}

export type PageMetadata = (params:any, shelvableBackend:ShelvableBackend, googleData:GoogleData) => {
    title:Observable<string>;
    description:Observable<string>;
    metaRobots:Observable<string[]>;
    breadcrumbs:Observable<string>;
};

export interface ShelvableStateDeclaration extends Ng2StateDeclaration {
    pageMetadata:PageMetadata;

    searchConfig:SearchConfig;
}

var injector:Injector;
var modalService:NgbModal;
var stateService:StateService;
var shelvableBackend:ShelvableBackend;
var currentParams:Observable<any>;

export function config(router: UIRouter, i:Injector, module:StatesModule) {
    injector = i;
    modalService = i.get(NgbModal);
    stateService = router.stateService;
    router.plugin(StickyStatesPlugin)
    router.urlService.config.strictMode(false);

    // use this to call decodeURIComponent on param inputs
    router.urlService.config.type('uricomponent', {
        encode: (p) => p,
        decode: (p) => decodeURIComponent(p),
        is: (a) => true,
        equals: (a, b) => (a === b),
        pattern: /[^/]+/
    });

    shelvableBackend = i.get(ShelvableBackend);

    var params = new BehaviorSubject<any>({});
    currentParams = params.asObservable();
    router.transitionService.onSuccess({}, (transition:Transition) => {
        params.next(transition.params());
    });

    PageMetadataService.initialise(router.transitionService, shelvableBackend, i.get(Meta), i.get(Title), i.get(GoogleData));
    Analytics.initialise(router.transitionService);
}

export interface ModalStateDeclaration extends ShelvableStateDeclaration {
    modalInstance?:NgbModalRef;
    upOnExit?:boolean;
    componentToLaunch:any; 
    modalSize?:"sm" | "lg" | "xl";
    modalIsClosed?:boolean,
    onEnter: (transition: Transition, state: ModalStateDeclaration) => void
    onExit: (transition: Transition, state: ModalStateDeclaration) => void
}

export function modalEnter(transition: Transition, state: ModalStateDeclaration) {
    // $stateParams.return() is called onExit.
    // it will either go up or back depending on whether the previous
    // state ($state.currentState) is the same as the parent state.
    // this is useful for when the user goes directly to a URL
    // that corresponds to a modal.
    state.modalInstance = modalService.open(state.componentToLaunch, {
        size: state.modalSize
    } as any);

    state.modalIsClosed = false;

    for (var key in transition.params()) {
        state.modalInstance.componentInstance[key] = transition.params()[key];
    }

    var currentState = transition.from().name;
    state.modalInstance.result.catch((reason) => {
        state.modalIsClosed = true;

        // note: use modal.$dismiss to return to previous url.
        // modal.$close will not go back (eg. for 'view your library' link)
        if (state['upOnExit']) {
            stateService.go('^');
        } else if (currentState) {
            window.history.back();
        } else {
            stateService.go('home');
        }
    });
}

export function modalExit(transition: Transition, state: ModalStateDeclaration) {
    if (!state.modalIsClosed) {
        state.modalInstance.close();
    }

    Utilities.reAddModalOpen();
}

export function getObservableOfFalse() {return Observable.of(false);} 

var modalBookState:ModalStateDeclaration = {
    name: 'book',
    url: '/b/:googleId',
    params: {fromScan: false},
    componentToLaunch: BookModalComponent,
    pageMetadata: modalBookMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    },
    onEnter: modalEnter,
    onExit: modalExit
};


export function modalBookMetadata(params:any, shelvableBackend:ShelvableBackend, googleData:GoogleData) {
    var bookPromise = googleData.getBook(params.googleId);

    return {
        metaRobots: Observable.fromPromise(bookPromise).map((book) => book ? [] : ['noindex']),
        description: Observable.fromPromise(bookPromise).map((book) => book ? (`Read about ${book.title} and save it to read later with Shelvable.`) : ''),
        title: Observable.fromPromise(bookPromise).map((book) => 'Shelvable: ' + book ? (book.title + (book.authors ? ' by ' + book.authors.join(', ') : '')) : 'Book not found'),
        breadcrumbs: Observable.fromPromise(bookPromise).map((book) => book ? createBookBreadcrumbs(book.googleId, book.authors, book.title) : '')
    }
}


export const googlePreviewState:ModalStateDeclaration = {
    name: 'googlePreviewState',
    parent: 'book',
    url: '/p',
    params: {preview_links: null, viewability: null, googleId: null},
    componentToLaunch: GoogleViewerComponent,
    modalSize: 'xl',
    pageMetadata: googlePreviewMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    },
    onEnter: modalEnter,
    onExit: modalExit
};

export function googlePreviewMetadata(params:any, shelvableBackend:ShelvableBackend, googleData:GoogleData) {
    return {
        metaRobots: Observable.of(['noindex']),
        description: Observable.of(''),
        title: Observable.fromPromise(googleData.getBook(params.googleId)).map((book) => `Shelvable: reading ${book.title} ${(book.authors ? `by ${book.authors.join(', ')}` : '')}`),
        breadcrumbs: Observable.of('')
    }
}

export const userManagementState:ModalStateDeclaration = {
    name: 'userManagement',
    url: '/u',
    params: {isGettingStarted: false},
    componentToLaunch: UserManagementComponent,
    pageMetadata: userManagementMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    },
    onEnter: modalEnter,
    onExit: modalExit
    //TODO: if ($state.findBookModal) { $state.findBookModal.close(); }
};

export function userManagementMetadata(params:any, shelvableBackend:ShelvableBackend) {
    return {
        metaRobots: Observable.of(['noindex']),
        description: Observable.of(''),
        title: Observable.of('Shelvable: your profile'),
        breadcrumbs: Observable.of('')
    }
}

export const userShelfSettingsState:ModalStateDeclaration = {
    name: 'userShelfSettings',
    parent: 'user',
    url: '/s/{shelfName:uricomponent}',
    params: {shelfName: null},
    componentToLaunch: ShelfSettingsComponent,
    modalSize: 'lg',
    upOnExit: true,
    pageMetadata: shelfSettingsMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    },
    onEnter: modalEnter,
    onExit: modalExit
};

export const shelfShelfSettingsState:ModalStateDeclaration = {
    name: 'shelfShelfSettings',
    parent: 'shelf',
    url: '/s/',
    componentToLaunch: ShelfSettingsComponent,
    modalSize: 'lg',
    upOnExit: true,
    pageMetadata: shelfSettingsMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    },
    onEnter: modalEnter,
    onExit: modalExit
};

export function shelfSettingsMetadata(params:any, shelvableBackend:ShelvableBackend) {
    return {
        metaRobots: Observable.of(['noindex']),
        description: Observable.of(''),
        title: Observable.of(`Shelvable: Settings for ${params.shelfName}`),
        breadcrumbs: Observable.of('')
    }
}

export const rootState:Ng2StateDeclaration = {
    name: 'root',
    url: "",
    sticky: true,
    abstract: true
};

export const privacyState:ShelvableStateDeclaration = {
    name: 'privacy',
    parent: 'root',
    url: '/privacy',
    views: {
        "@": {
            component: PrivacyComponent
        }
    },
    pageMetadata: privacyStateMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    }
};

export function privacyStateMetadata(params:any, shelvableBackend:ShelvableBackend) {
    return {
        metaRobots: Observable.of([]),
        description: Observable.of(''),
        title: Observable.of('Shelvable: Privacy Policy'),
        breadcrumbs: Observable.of('')
    }
}

export const homeState:ShelvableStateDeclaration = {
    name: 'home',
    parent: 'root',
    url: '/',
    views: {
        '@': {
            component: HomeComponent
        }
    },
    pageMetadata: homepageMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    }
};

export function homepageMetadata(params:any, shelvableBackend:ShelvableBackend) {
    return {
        metaRobots: Observable.of([]),
        description: Observable.of("Shelvable helps you keep track of books you want to read."),
        title: Observable.of('Shelvable.'),
        breadcrumbs: Observable.of('')
    }
}

export const fullSearchState:ShelvableStateDeclaration = {
    name: 'search',
    parent: 'root',
    url: '/search?q',
    views: {
        "@": {
            component: FullSearchComponent
        }
    },
    pageMetadata: fullSearchMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    }
}


export function fullSearchMetadata(params:any, shelvableBackend:ShelvableBackend) {
    return {
        metaRobots: Observable.of([]),
        description: Observable.of('Search for your next favourite book on Shelvable.'),
        title: Observable.of(`Shelvable: searching for ${params.q}`),
        breadcrumbs: Observable.of('')
    }
}

export const barcodeScannerState:ShelvableStateDeclaration = {
    name: 'scan',
    parent: 'root',
    url: '/scan',
    views: {
        "@": {
            component: BarcodeScannerComponent
        }
    },
    pageMetadata: barcodeScannerMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    }
}

export function barcodeScannerMetadata() {
    return {
        metaRobots: Observable.of([]),
        description: Observable.of('Scan a book\'s barcode and save it to read later with Shelvable.'),
        title: Observable.of(`Shelvable: barcode scanner`),
        breadcrumbs: Observable.of('')
    }
}


export const libraryState:ShelvableStateDeclaration = {
    name: 'user',
    parent: 'root',
    url: '/:username',
    views: {
        "@": {
            component: UserLibraryComponent
        }
    },
    resolve: [{
        token: 'userId',
        deps: [Transition, ShelvableBackend],
        resolveFn: resolveUserIdFromTransitionAndBackend
    }],
    pageMetadata: libraryMetadata,
    searchConfig: {
        visible: librarySearchVisible,
        rightButtonType: 'add-multi'
    }
};

export function librarySearchVisible() {
    return currentParams.flatMap((params) => {
        if (params && params.username) {
            return Observable.fromPromise(shelvableBackend.getUserId(params.username))
                .map((userId) => userId === shelvableBackend.loggedInUserId.getValue());
        } else {
            return Observable.of(false);
        }
    })
}

export function resolveUserIdFromTransitionAndBackend(trans:Transition, shelvableBackend:ShelvableBackend) {return shelvableBackend.getUserId(trans.params().username)}

export function libraryMetadata(params:any, shelvableBackend:ShelvableBackend) {
    var userIdObservable = Observable.fromPromise(shelvableBackend.getUserId(params.username));
    var userDataObservable = userIdObservable.flatMap((userId) => userId ? shelvableBackend.getUserData(userId) : Observable.of(null));
    return {
        metaRobots: userIdObservable.map((userId) => {
            if (!userId) {
                return ['noindex']
            } else {
                return [];
            }
        }),
        description: userIdObservable.flatMap((userId) => {
            if (!userId) {
                return Observable.of('');
            } else {
                return userDataObservable.map((userData) => `${(userData ? userData.displayName : null) || params.username} is using Shelvable to keep track of books to read.`)
            }
        }),
        title: userIdObservable.flatMap((userId) => {
            if (!userId) {
                return Observable.of('Shelvable: not found');
            } else {
                return userDataObservable.map((userData) => `Shelvable: ${((userData ? userData.displayName : null) || params.username)}'s library`);
            }
        }),
        breadcrumbs: userDataObservable.map((userData) => {
            return createBreadcrumbs(params.username, userData ? userData.displayName : null, null);
        })
    }
}


export const shelfState:ShelvableStateDeclaration = {
    name: 'shelf',
    parent: 'root',
    url: '/:username/{shelfName:uricomponent}',
    views: {
        "@": {
            component: UserShelfComponent
        }
    },
    resolve: [{
        token: 'userId',
        deps: [Transition, ShelvableBackend],
        resolveFn: resolveUserIdFromTransitionAndBackend
    }],
    pageMetadata: shelfMetadata,
    searchConfig: {
        visible: shelfSearchVisible,
        rightButtonType: 'add-single'
    }
}

export function shelfSearchVisible() {
    return currentParams.flatMap((params) => {
        if (params && params.username && params.shelfName) {
            return Observable.fromPromise(shelvableBackend.getUserId(params.username)).flatMap((userId) => {
                if (!userId) return Observable.of(false);

                return shelvableBackend.doesShelfExist(userId, params.shelfName);
            });
        } else {
            return Observable.of(false);
        }
    })
}

export function shelfMetadata(params:any, shelvableBackend:ShelvableBackend) {
    var userIdObservable = Observable.fromPromise(shelvableBackend.getUserId(params.username));
    var userDataObservable = userIdObservable.flatMap((userId) => userId ? shelvableBackend.getUserData(userId) : Observable.of(null)).shareReplay(1);
    var pageExistsObservable = userIdObservable.flatMap((userId) => {
        if (userId) {
            return shelvableBackend.doesShelfExist(userId, params.shelfName);
        } else {
            return Observable.of(false);
        }
    }).shareReplay(1);
    return {
        metaRobots: pageExistsObservable.map((pageExists) => {
            if (pageExists) {
                return [];
            } else {
                return ['noindex'];
            }
        }),
        description: pageExistsObservable.flatMap((pageExists) => {
            if (!pageExists) {
                return Observable.of('');
            } else {
                return userDataObservable.map((userData) => `${(userData ? userData.displayName : null) || params.username} is using Shelvable to organise books on a shelf called ${params.shelfName}.`);
            }
        }),
        title: pageExistsObservable.flatMap((pageExists) => {
            if (!pageExists) {
                return Observable.of('Shelvable: not found');
            } else {
                return userDataObservable.map((userData) => `Shelvable: ${params.username}'s ${params.shelfName} shelf`);
            }
        }),
        breadcrumbs: userDataObservable.map((userData) => {
            return createBreadcrumbs(params.username, userData ? userData.displayName : null, params.shelfName);
        })
    }
}


export const exploreAuthorState:ShelvableStateDeclaration = {
    name: 'exploreAuthor',
    parent: 'root',
    url: '/explore/author/:query',
    views: {
        "@": {
            component: ExploreAuthorComponent
        }
    },
    pageMetadata: exploreAuthorMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    }
};

export const exploreAuthorGoState:Ng2StateDeclaration = {
    name: 'exploreAuthorGo',
    parent: 'root',
    url: '/explore/author/go?kw',
    redirectTo: exploreAuthorGoRedirect
};

export async function exploreAuthorGoRedirect(trans:Transition):Promise<RedirectToResult> {
    var keyword = trans.params().kw;
    if (!keyword) {
        return {state: 'home'};
    } else {
        try {
            var author = await shelvableBackend.getExploreAuthorByKeyword(decodeURIComponent(keyword));
            return {
                state: 'exploreAuthor', 
                params: {query: author.url}
            }
        } catch (err) {
            console.error('Unable to find author page for keyword');
            return {state: 'home'}
        }
    }
}

export function exploreAuthorMetadata(params:any, shelvableBackend:ShelvableBackend) {
    var author = Observable.fromPromise(shelvableBackend.getExploreAuthorByQuery(params.query));
    return {
        metaRobots: author.map((author) => author ? [] : ['noindex']),
        description: author.map((author) => `Explore books by author ${author.name} and add them to shelves to read later.`),
        title: author.map((author) => `Shelvable: exploring author ${author.name}`),
        breadcrumbs: author.map((author) => author ? createExploreBreadcrumbs(author.name, author.url) : '')
    }
}

export const exploreSubjectState = {
    name: 'exploreSubject',
    parent: 'root',
    url: '/explore/subject/:query',
    views: {
        "@": {
            component: "exploreSubject"                              
        }
    },
    searchConfig: {
        visible: getObservableOfFalse
    }
}

export const errorState:ShelvableStateDeclaration = {
    name: 'error',
    url: '*path',
    views: {
        "@": {
            component: ErrorPageComponent
        }
    },
    pageMetadata: errorMetadata,
    searchConfig: {
        visible: getObservableOfFalse
    }
};

export function errorMetadata(params:any, shelvableBackend:ShelvableBackend) {
    return {
        metaRobots: Observable.of(['noindex']),
        description: Observable.of(''),
        title: Observable.of('Shelvable: not found'),
        breadcrumbs: Observable.of('')
    }
}

export const indexState:Ng2StateDeclaration = {
    name: 'indexState',
    url: '/pwa.html',
    redirectTo: 'home'
}


export const States:Ng2StateDeclaration[] = [
    indexState,
    rootState, 
    homeState, 
    
    {
        name: 'oneLetterSearchRedirect',
        url: '/s/search?q',
        redirectTo: 'search'
    },
    fullSearchState, 

    barcodeScannerState,
    modalBookState, 
    googlePreviewState, 
    userManagementState, 
    
    {
        name: 'oneLetterPrivacyRedirect',
        url: '/s/privacy',
        redirectTo: 'privacy'
    },
    privacyState, 
    
    errorState, 

    {
        name: 'oneLetterAuthorGoRedirect',
        url: '/e/author/go?kw',
        redirectTo: 'exploreAuthorGo'
    },
    exploreAuthorGoState,

    {
        name: 'oneLetterAuthorRedirect',
        url: '/e/author/:query',
        redirectTo: 'exploreAuthor'
    },
    exploreAuthorState,
    
    libraryState,
    shelfState,
    userShelfSettingsState,
    shelfShelfSettingsState
]


// WEBPACK FOOTER //
// ./web/static/states.ts